import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import ConfirmModal from "../../common/ConfirmModal";
import Dropdown from "../../common/dropDown";
import SnomedSearchField from "./SnomedSearchField";
import {
  archiveMedicationHistory,
  fetchFamilyRelations,
  loadParticipantClinicalPack,
  saveAllergyHistory,
  saveFamilyHistory,
  saveMedicalHistory,
  saveMedicationHistory,
  savePhysicalMeasurementHistory,
  saveSurgicalHistory,
} from "./participantService";
import {
  CLINICAL_SECTIONS,
  STATUS_OPTIONS,
  buildConditionAuditFields,
  buildMedicationAuditFields,
  buildPhysicalAuditFields,
  calcBmi,
  displayOrDash,
  parseUiDate,
  pickField,
  toApiDate,
  toChangedFormFieldsJson,
} from "./clinicalHistoryUtils";
import { getParticipantAgeYears } from "./participantReportUtils";
import { MedicationModal, medId } from "./MedicationHistoryPanel";

const SECTION_ICONS = {
  physical: "fa-ruler-vertical",
  medical: "fa-notes-medical",
  surgical: "fa-procedures",
  family: "fa-users",
  allergy: "fa-allergies",
  medication: "fa-pills",
};

function rowId(row, idKey) {
  return String(pickField(row, idKey, idKey.replace(/Id$/, "id")) || "").trim();
}

function ConditionModal({
  open,
  section,
  initial,
  userData,
  relations,
  saving,
  onClose,
  onSave,
}) {
  const [snomed, setSnomed] = useState(null);
  const [onset, setOnset] = useState(null);
  const [status, setStatus] = useState("Active");
  const [resolution, setResolution] = useState(null);
  const [notAvailable, setNotAvailable] = useState(false);
  const [relationship, setRelationship] = useState("");
  const [relationshipId, setRelationshipId] = useState("");

  useEffect(() => {
    if (!open) return;
    const term = String(pickField(initial, "SnomedTerm") || "").trim();
    const code = String(
      pickField(initial, "SnomedCode", "SnomedId") || "",
    ).trim();
    setSnomed(term ? { term, code } : null);
    setOnset(parseUiDate(pickField(initial, "DateOfOnSet", "OnSetDate", "LKODate")));
    setResolution(
      parseUiDate(pickField(initial, "DateOfResolution", "ResolutionDate")),
    );
    const st = String(pickField(initial, "Status") || "Active").trim();
    setStatus(st && st.toLowerCase() !== "not available" ? st : "Active");
    setNotAvailable(
      String(pickField(initial, "IsApplicable") || "") === "1" ||
        String(pickField(initial, "IsApplicable") || "").toLowerCase() === "true",
    );
    const rel = String(
      pickField(initial, "RelationShip", "Relationship") || "",
    ).trim();
    setRelationship(rel);
    setRelationshipId(
      String(
        pickField(initial, "RelationShipId", "RelationshipId") || "",
      ).trim(),
    );
  }, [initial, open]);

  if (!open) return null;

  const isFamily = section.key === "family";
  const onsetLabel = section.onsetLabel || "Onset date";
  const editing = Boolean(initial && rowId(initial, section.idKey));

  return createPortal(
    <div className="pm-clinical-modal" role="dialog" aria-modal="true">
      <div className="pm-clinical-modal__dialog">
        <header className="pm-clinical-modal__header">
          <h3 className="pm-clinical-modal__title">
            {editing ? "Edit" : "Add"} {section.title.toLowerCase()}
          </h3>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            disabled={saving}
            onClick={onClose}
          />
        </header>
        <div className="pm-clinical-modal__body">
          <SnomedSearchField
            userData={userData}
            termType={section.termType}
            label="Condition"
            selected={snomed}
            onSelect={setSnomed}
            disabled={saving}
          />

          {isFamily ? (
            <div className="pm-clinical-modal__field">
              <label className="pm-clinical-modal__label">Relationship</label>
              <Dropdown
                options={[
                  { value: "", label: "Select relationship" },
                  ...relations
                    .map((r) => {
                      const term = String(
                        pickField(r, "Term", "SnomedTerm") || "",
                      ).trim();
                      if (!term) return null;
                      return { value: term, label: term };
                    })
                    .filter(Boolean),
                ]}
                selected={
                  relationship
                    ? { value: relationship, label: relationship }
                    : { value: "", label: "Select relationship" }
                }
                onSelect={(opt) => {
                  const value = String(opt?.value || "");
                  setRelationship(value);
                  const match = relations.find(
                    (r) =>
                      String(pickField(r, "Term") || "") === value ||
                      String(pickField(r, "SnomedTerm") || "") === value,
                  );
                  setRelationshipId(
                    String(
                      pickField(match, "SnomedCode", "SenomedCode", "GUIDPK") ||
                        "",
                    ),
                  );
                }}
                showSearch
                disabled={saving}
              />
            </div>
          ) : null}

          <label className="pm-clinical-modal__check">
            <input
              type="checkbox"
              checked={notAvailable}
              disabled={saving}
              onChange={(e) => setNotAvailable(e.target.checked)}
            />
            Not available
          </label>

          {!notAvailable ? (
            <>
              <div className="pm-clinical-modal__row">
                <div className="pm-clinical-modal__field">
                  <label className="pm-clinical-modal__label">{onsetLabel}</label>
                  <DatePicker
                    selected={onset}
                    onChange={setOnset}
                    dateFormat="dd/MM/yyyy"
                    className="form-control form-control-sm"
                    placeholderText="DD/MM/YYYY"
                    disabled={saving}
                    isClearable
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    todayButton="Today"
                    minDate={new Date(1920, 0, 1)}
                    maxDate={new Date()}
                    popperProps={{ strategy: "fixed" }}
                  />
                </div>
                <div className="pm-clinical-modal__field">
                  <label className="pm-clinical-modal__label">Status</label>
                  <Dropdown
                    options={STATUS_OPTIONS.map((opt) => ({
                      value: opt.value,
                      label: opt.label,
                    }))}
                    selected={
                      STATUS_OPTIONS.find((o) => o.value === status) ||
                      STATUS_OPTIONS[0]
                    }
                    onSelect={(opt) => setStatus(opt?.value || "Active")}
                    showSearch={false}
                    disabled={saving}
                  />
                </div>
              </div>
              {status === "Resolved" ? (
                <div className="pm-clinical-modal__field">
                  <label className="pm-clinical-modal__label">
                    Resolution date
                  </label>
                  <DatePicker
                    selected={resolution}
                    onChange={setResolution}
                    dateFormat="dd/MM/yyyy"
                    className="form-control form-control-sm"
                    placeholderText="DD/MM/YYYY"
                    disabled={saving}
                    isClearable
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    todayButton="Today"
                    minDate={onset || new Date(1920, 0, 1)}
                    maxDate={new Date()}
                    popperProps={{ strategy: "fixed" }}
                  />
                </div>
              ) : null}
            </>
          ) : null}
        </div>
        <footer className="pm-clinical-modal__footer">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={saving}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={saving || !snomed?.term}
            onClick={() =>
              onSave({
                snomed,
                onset,
                status,
                resolution,
                notAvailable,
                relationship,
                relationshipId,
              })
            }
          >
            {saving ? "Saving…" : editing ? "Update" : "Save"}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

function PhysicalModal({
  open,
  initial,
  volunteer,
  saving,
  onClose,
  onSave,
}) {
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");

  useEffect(() => {
    if (!open) return;
    setHeight(String(pickField(initial, "Height") || ""));
    setWeight(String(pickField(initial, "Weight", "weight") || ""));
  }, [initial, open]);

  if (!open) return null;
  const bmi = calcBmi(height, weight);
  const age = getParticipantAgeYears(volunteer?.DOB) ?? "";
  const gender = String(pickField(volunteer, "Gender") || "").trim();
  const editing = Boolean(
    initial && rowId(initial, "PhysicalMeasurementHistoryId"),
  );

  return createPortal(
    <div className="pm-clinical-modal" role="dialog" aria-modal="true">
      <div className="pm-clinical-modal__dialog">
        <header className="pm-clinical-modal__header">
          <h3 className="pm-clinical-modal__title">
            {editing ? "Edit" : "Add"} physical measurement
          </h3>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            disabled={saving}
            onClick={onClose}
          />
        </header>
        <div className="pm-clinical-modal__body">
          <div className="pm-clinical-modal__row">
            <div className="pm-clinical-modal__field">
              <label className="pm-clinical-modal__label">Age</label>
              <input
                className="form-control form-control-sm"
                value={age === "" ? "—" : age}
                disabled
              />
            </div>
            <div className="pm-clinical-modal__field">
              <label className="pm-clinical-modal__label">Gender</label>
              <input
                className="form-control form-control-sm"
                value={gender || "—"}
                disabled
              />
            </div>
          </div>
          <div className="pm-clinical-modal__row">
            <div className="pm-clinical-modal__field">
              <label className="pm-clinical-modal__label">Height (cm)</label>
              <input
                type="number"
                min="0"
                step="0.1"
                className="form-control form-control-sm"
                value={height}
                disabled={saving}
                onChange={(e) => setHeight(e.target.value)}
              />
            </div>
            <div className="pm-clinical-modal__field">
              <label className="pm-clinical-modal__label">Weight (kg)</label>
              <input
                type="number"
                min="0"
                step="0.1"
                className="form-control form-control-sm"
                value={weight}
                disabled={saving}
                onChange={(e) => setWeight(e.target.value)}
              />
            </div>
          </div>
          <div className="pm-clinical-modal__field">
            <label className="pm-clinical-modal__label">BMI</label>
            <input
              className="form-control form-control-sm"
              value={bmi || "—"}
              disabled
            />
          </div>
        </div>
        <footer className="pm-clinical-modal__footer">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={saving}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={saving || !height || !weight}
            onClick={() => onSave({ height, weight, bmi, age, gender })}
          >
            {saving ? "Saving…" : editing ? "Update" : "Save"}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

function ClinicalHistoryPanel({
  userData,
  volunteerId,
  volunteer,
  canEdit = false,
  showAlert,
  showLoader,
  hideLoader,
  initialSection,
  onChanged,
}) {
  const emptyPack = {
    medical: [],
    surgical: [],
    family: [],
    allergy: [],
    physical: [],
    medication: [],
  };
  const [pack, setPack] = useState(emptyPack);
  const [openKey, setOpenKey] = useState(() => {
    const key = String(initialSection || "").trim();
    return CLINICAL_SECTIONS.some((s) => s.key === key) ? key : "physical";
  });
  const [loading, setLoading] = useState(false);
  const [relations, setRelations] = useState([]);
  const [modal, setModal] = useState(null); // { sectionKey, row? }
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const key = String(initialSection || "").trim();
    if (CLINICAL_SECTIONS.some((s) => s.key === key)) setOpenKey(key);
  }, [initialSection]);

  const reload = useCallback(async () => {
    if (!userData?.OrganizationId || !volunteerId) return;
    setLoading(true);
    try {
      const next = await loadParticipantClinicalPack(userData, volunteerId);
      setPack({
        medical: next.medical || [],
        surgical: next.surgical || [],
        family: next.family || [],
        allergy: next.allergy || [],
        physical: next.physical || [],
        medication: next.medication || [],
      });
    } catch {
      setPack(emptyPack);
    } finally {
      setLoading(false);
    }
  }, [userData, volunteerId]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    if (!userData?.OrganizationId) return;
    fetchFamilyRelations(userData)
      .then((rows) => setRelations(Array.isArray(rows) ? rows : []))
      .catch(() => setRelations([]));
  }, [userData]);

  const sectionMap = useMemo(
    () => Object.fromEntries(CLINICAL_SECTIONS.map((s) => [s.key, s])),
    [],
  );

  const handleSaveCondition = async (form) => {
    const section = sectionMap[modal?.sectionKey];
    if (!section || !form?.snomed?.term) return;
    if (
      !form.notAvailable &&
      form.status === "Resolved" &&
      form.onset &&
      form.resolution &&
      form.resolution < form.onset
    ) {
      showAlert?.({
        message: "Resolution date cannot be before onset date.",
        type: "warning",
      });
      return;
    }

    const id = modal.row ? rowId(modal.row, section.idKey) : "";
    const actionType = id ? "update" : "insert";
    const onsetApi = form.notAvailable ? null : toApiDate(form.onset) || null;
    const resolutionApi =
      form.notAvailable || form.status !== "Resolved"
        ? null
        : toApiDate(form.resolution) || null;
    const changedFormFields = toChangedFormFieldsJson(
      buildConditionAuditFields(section, modal.row || null, form),
    );
    const base = {
      VolunteerIdRef: volunteerId,
      SnomedId: form.snomed.code || "",
      SnomedTerm: form.snomed.term,
      OnSetDate: onsetApi,
      ResolutionDate: resolutionApi,
      IsApplicable: form.notAvailable ? 1 : 0,
      Status: form.notAvailable ? "" : form.status,
      ActionType: actionType,
      ChangedFormFields: changedFormFields,
    };

    setSaving(true);
    showLoader?.("Saving…");
    try {
      if (section.key === "medical") {
        await saveMedicalHistory(userData, {
          ...base,
          MedicalHistoryId: id || null,
        });
      } else if (section.key === "surgical") {
        await saveSurgicalHistory(userData, {
          ...base,
          SurgicalHistoryId: id || null,
        });
      } else if (section.key === "family") {
        await saveFamilyHistory(userData, {
          ...base,
          FamilyHistoryId: id || null,
          RelationShip: form.relationship || "",
          RelationShipId: form.relationshipId || "",
        });
      } else if (section.key === "allergy") {
        await saveAllergyHistory(userData, {
          ...base,
          AllergyHistoryId: id || null,
          Adverse: "",
          AdverseId: "",
        });
      }
      setModal(null);
      showAlert?.({ message: "Saved.", type: "success" });
      await reload();
      await onChanged?.();
    } catch (err) {
      showAlert?.({ message: err?.message || "Could not save." });
    } finally {
      hideLoader?.();
      setSaving(false);
    }
  };

  const handleSavePhysical = async (form) => {
    const id = modal?.row
      ? rowId(modal.row, "PhysicalMeasurementHistoryId")
      : "";
    setSaving(true);
    showLoader?.("Saving…");
    try {
      await savePhysicalMeasurementHistory(userData, {
        PhysicalMeasurementHistoryId: id || null,
        VolunteerIdRef: volunteerId,
        Age: Number(form.age) || 0,
        Gender: form.gender || "",
        Height: Number(form.height) || 0,
        weight: Number(form.weight) || 0,
        Weight: Number(form.weight) || 0,
        BMI: form.bmi || "",
        ColorCode: "#27B01D",
        ActionType: id ? "update" : "insert",
        ChangedFormFields: toChangedFormFieldsJson(
          buildPhysicalAuditFields(modal?.row || null, form),
        ),
      });
      setModal(null);
      showAlert?.({ message: "Saved.", type: "success" });
      await reload();
      await onChanged?.();
    } catch (err) {
      showAlert?.({ message: err?.message || "Could not save." });
    } finally {
      hideLoader?.();
      setSaving(false);
    }
  };

  const handleSaveMedication = async (form) => {
    if (!form?.medication?.term) {
      showAlert?.({ message: "Please select a medication." });
      return;
    }
    if (form.startDate && form.endDate && form.endDate < form.startDate) {
      showAlert?.({
        message: "End date cannot be before start date.",
        type: "warning",
      });
      return;
    }
    const id = modal?.row ? medId(modal.row) : "";
    setSaving(true);
    showLoader?.("Saving medication…");
    try {
      await saveMedicationHistory(userData, {
        MedicationHistoryId: id || null,
        VolunteerId: volunteerId,
        MedicationName: form.medication.term,
        MedicationSnomedCode: form.medication.code || null,
        Route: form.route?.term || "",
        Dose: form.dose || "",
        Unit: form.unit?.term || "",
        UnitSnomedCode: form.unit?.code || "",
        Frequency: form.frequency?.term || "",
        FrequencySnomedCode: form.frequency?.code || "",
        Indication: form.indication || "",
        IndicationSnomedCode: form.indicationCode || "",
        StartDate: toApiDate(form.startDate) || null,
        ResolutionDate: toApiDate(form.endDate) || null,
        IsArchive: false,
        ActionType: "InsertUpdate",
        ChangedFormFields: toChangedFormFieldsJson(
          buildMedicationAuditFields(modal?.row || null, form),
        ),
      });
      setModal(null);
      showAlert?.({ message: "Medication saved.", type: "success" });
      await reload();
      await onChanged?.();
    } catch (err) {
      showAlert?.({ message: err?.message || "Could not save medication." });
    } finally {
      hideLoader?.();
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    const section = sectionMap[deleteTarget?.sectionKey];
    if (!section || !deleteTarget?.id) return;
    setDeleting(true);
    showLoader?.("Deleting…");
    try {
      if (section.key === "medication") {
        await archiveMedicationHistory(userData, {
          medicationHistoryId: deleteTarget.id,
          volunteerId,
        });
      } else {
        const base = {
          VolunteerIdRef: volunteerId,
          ActionType: "delete",
          [section.idKey]: deleteTarget.id,
        };
        if (section.key === "medical") await saveMedicalHistory(userData, base);
        else if (section.key === "surgical")
          await saveSurgicalHistory(userData, base);
        else if (section.key === "family")
          await saveFamilyHistory(userData, base);
        else if (section.key === "allergy")
          await saveAllergyHistory(userData, base);
        else if (section.key === "physical")
          await savePhysicalMeasurementHistory(userData, base);
      }
      setDeleteTarget(null);
      showAlert?.({ message: "Deleted.", type: "success" });
      await reload();
      await onChanged?.();
    } catch (err) {
      showAlert?.({ message: err?.message || "Could not delete." });
    } finally {
      hideLoader?.();
      setDeleting(false);
    }
  };

  const activeSection = modal ? sectionMap[modal.sectionKey] : null;
  const currentSection =
    CLINICAL_SECTIONS.find((s) => s.key === openKey) || CLINICAL_SECTIONS[0];
  const currentRows = pack[currentSection.key] || [];

  return (
    <div className="pm-clinical-tab">
      <div
        className="pm-clinical-chips"
        role="tablist"
        aria-label="Clinical sections"
      >
        {CLINICAL_SECTIONS.map((section) => {
          const count = (pack[section.key] || []).length;
          const active = openKey === section.key;
          return (
            <button
              key={section.key}
              type="button"
              role="tab"
              aria-selected={active}
              className={`pm-clinical-chip${active ? " is-active" : ""}`}
              onClick={() => setOpenKey(section.key)}
            >
              <i
                className={`fas ${SECTION_ICONS[section.key] || "fa-circle"}`}
                aria-hidden="true"
              />
              <span>{section.title}</span>
              <em>{count}</em>
            </button>
          );
        })}
      </div>

      <div className="pm-clinical-body">
      {currentRows.length || loading ? (
        <div className="pm-clinical-toolbar">
          {loading ? (
            <span className="pm-clinical-tab__muted">Loading…</span>
          ) : (
            <span className="pm-clinical-toolbar__label">
              {currentSection.title}
            </span>
          )}
          {canEdit && currentRows.length ? (
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() =>
                setModal({ sectionKey: currentSection.key, row: null })
              }
            >
              <i className="fas fa-plus me-1" aria-hidden="true" />
              Add
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="pm-clinical-panel">
        {currentRows.length ? (
          <div className="pm-clinical-cards pm-thin-scroll">
            {currentSection.key === "medication"
              ? currentRows.map((row, index) => {
                  const id = medId(row);
                  const dose = displayOrDash(pickField(row, "Dose"));
                  const unit = displayOrDash(pickField(row, "Unit"));
                  const doseLabel =
                    dose !== "—" || unit !== "—"
                      ? [dose !== "—" ? dose : null, unit !== "—" ? unit : null]
                          .filter(Boolean)
                          .join(" ")
                      : "—";
                  return (
                    <article key={id || index} className="pm-clinical-card">
                      <div
                        className="pm-clinical-card__icon"
                        aria-hidden="true"
                      >
                        <i className="fas fa-pills" />
                      </div>
                      <div className="pm-clinical-card__body">
                        <h4 className="pm-clinical-card__title">
                          {displayOrDash(pickField(row, "MedicationName"))}
                        </h4>
                        <div className="pm-clinical-card__meta">
                          <span>{doseLabel}</span>
                          <span>
                            {displayOrDash(pickField(row, "Frequency"))}
                          </span>
                          <span>{displayOrDash(pickField(row, "Route"))}</span>
                          <span>
                            {displayOrDash(pickField(row, "StartDate"))}
                            {" → "}
                            {displayOrDash(pickField(row, "ResolutionDate"))}
                          </span>
                          {pickField(row, "Indication") ? (
                            <span>
                              {displayOrDash(pickField(row, "Indication"))}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      {canEdit ? (
                        <div className="pm-clinical-card__actions">
                          <button
                            type="button"
                            className="pm-clinical-card__btn"
                            title="Edit"
                            aria-label="Edit"
                            onClick={() =>
                              setModal({ sectionKey: "medication", row })
                            }
                          >
                            <i className="fas fa-pencil-alt" />
                          </button>
                          <button
                            type="button"
                            className="pm-clinical-card__btn is-danger"
                            title="Delete"
                            aria-label="Delete"
                            disabled={!id}
                            onClick={() =>
                              setDeleteTarget({
                                sectionKey: "medication",
                                id,
                              })
                            }
                          >
                            <i className="fas fa-trash-alt" />
                          </button>
                        </div>
                      ) : null}
                    </article>
                  );
                })
              : currentSection.key === "physical"
              ? currentRows.map((row, index) => {
                  const id = rowId(row, "PhysicalMeasurementHistoryId");
                  return (
                    <article key={id || index} className="pm-clinical-card">
                      <div
                        className="pm-clinical-card__icon"
                        aria-hidden="true"
                      >
                        <i className="fas fa-ruler-vertical" />
                      </div>
                      <div className="pm-clinical-card__body">
                        <h4 className="pm-clinical-card__title">
                          BMI {displayOrDash(pickField(row, "BMI"))}
                        </h4>
                        <div className="pm-clinical-card__meta">
                          <span>
                            Age {displayOrDash(pickField(row, "Age"))}
                          </span>
                          <span>
                            {displayOrDash(pickField(row, "Height"))} cm
                          </span>
                          <span>
                            {displayOrDash(pickField(row, "Weight", "weight"))}{" "}
                            kg
                          </span>
                          <span>
                            {displayOrDash(pickField(row, "SystemDateTime"))}
                          </span>
                        </div>
                      </div>
                      {canEdit ? (
                        <div className="pm-clinical-card__actions">
                          <button
                            type="button"
                            className="pm-clinical-card__btn"
                            title="Edit"
                            aria-label="Edit"
                            onClick={() =>
                              setModal({ sectionKey: "physical", row })
                            }
                          >
                            <i className="fas fa-pencil-alt" />
                          </button>
                          <button
                            type="button"
                            className="pm-clinical-card__btn is-danger"
                            title="Delete"
                            aria-label="Delete"
                            onClick={() =>
                              setDeleteTarget({
                                sectionKey: "physical",
                                id,
                              })
                            }
                          >
                            <i className="fas fa-trash-alt" />
                          </button>
                        </div>
                      ) : null}
                    </article>
                  );
                })
              : currentRows.map((row, index) => {
                  const id = rowId(row, currentSection.idKey);
                  const status = String(pickField(row, "Status") || "").trim();
                  const statusKey = status.toLowerCase();
                  return (
                    <article key={id || index} className="pm-clinical-card">
                      <div
                        className="pm-clinical-card__icon"
                        aria-hidden="true"
                      >
                        <i
                          className={`fas ${
                            SECTION_ICONS[currentSection.key] || "fa-notes-medical"
                          }`}
                        />
                      </div>
                      <div className="pm-clinical-card__body">
                        <div className="pm-clinical-card__title-row">
                          <h4 className="pm-clinical-card__title">
                            {displayOrDash(pickField(row, "SnomedTerm"))}
                          </h4>
                          {status && status !== "—" ? (
                            <span
                              className={`pm-clinical-pill${
                                statusKey === "resolved"
                                  ? " is-resolved"
                                  : statusKey === "active"
                                    ? " is-active"
                                    : ""
                              }`}
                            >
                              {status}
                            </span>
                          ) : null}
                        </div>
                        <div className="pm-clinical-card__meta">
                          {currentSection.key === "family" ? (
                            <span>
                              {displayOrDash(
                                pickField(
                                  row,
                                  "RelationShip",
                                  "Relationship",
                                ),
                              )}
                            </span>
                          ) : null}
                          <span>
                            {currentSection.onsetLabel || "Onset"}{" "}
                            {displayOrDash(
                              pickField(
                                row,
                                "DateOfOnSet",
                                "OnSetDate",
                                "LKODate",
                              ),
                            )}
                          </span>
                          <span>
                            Resolved{" "}
                            {displayOrDash(
                              pickField(
                                row,
                                "DateOfResolution",
                                "ResolutionDate",
                              ),
                            )}
                          </span>
                        </div>
                      </div>
                      {canEdit ? (
                        <div className="pm-clinical-card__actions">
                          <button
                            type="button"
                            className="pm-clinical-card__btn"
                            title="Edit"
                            aria-label="Edit"
                            onClick={() =>
                              setModal({
                                sectionKey: currentSection.key,
                                row,
                              })
                            }
                          >
                            <i className="fas fa-pencil-alt" />
                          </button>
                          <button
                            type="button"
                            className="pm-clinical-card__btn is-danger"
                            title="Delete"
                            aria-label="Delete"
                            onClick={() =>
                              setDeleteTarget({
                                sectionKey: currentSection.key,
                                id,
                              })
                            }
                          >
                            <i className="fas fa-trash-alt" />
                          </button>
                        </div>
                      ) : null}
                    </article>
                  );
                })}
          </div>
        ) : (
          <div className="pm-clinical-empty">
            <div className="pm-clinical-empty__icon" aria-hidden="true">
              <i
                className={`fas ${
                  SECTION_ICONS[currentSection.key] || "fa-notes-medical"
                }`}
              />
            </div>
            <p className="pm-clinical-empty__title">
              No {currentSection.title.toLowerCase()} yet
            </p>
            <p className="pm-clinical-empty__text">
              {canEdit
                ? "Add the first record to start building this history."
                : "Nothing recorded for this section."}
            </p>
            {canEdit ? (
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() =>
                  setModal({ sectionKey: currentSection.key, row: null })
                }
              >
                <i className="fas fa-plus me-1" aria-hidden="true" />
                Add {currentSection.title.toLowerCase()}
              </button>
            ) : null}
          </div>
        )}
      </div>
      </div>

      {activeSection?.key === "medication" ? (
        <MedicationModal
          open={Boolean(modal)}
          initial={modal?.row}
          userData={userData}
          medicalIndications={pack.medical || []}
          saving={saving}
          onClose={() => !saving && setModal(null)}
          onSave={handleSaveMedication}
        />
      ) : activeSection?.key === "physical" ? (
        <PhysicalModal
          open={Boolean(modal)}
          initial={modal?.row}
          volunteer={volunteer}
          saving={saving}
          onClose={() => !saving && setModal(null)}
          onSave={handleSavePhysical}
        />
      ) : activeSection ? (
        <ConditionModal
          open={Boolean(modal)}
          section={activeSection}
          initial={modal?.row}
          userData={userData}
          relations={relations}
          saving={saving}
          onClose={() => !saving && setModal(null)}
          onSave={handleSaveCondition}
        />
      ) : null}

      <ConfirmModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title="Delete record"
        message="Are you sure you want to delete this clinical history record?"
        confirmText={deleting ? "Deleting…" : "Delete"}
        confirmButtonClass="btn-danger"
        icon="fas fa-trash-alt text-danger"
      />
    </div>
  );
}

export default ClinicalHistoryPanel;
